import { Box, Callout, Flex } from '@radix-ui/themes';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import Icon from '~/components/Icon';
import { getErrorMessageFromAPIError } from '~/lib/error';

const StyledCalloutRoot = styled(Callout.Root)`
  border: 1px solid var(--error-6);
  border-radius: var(--radius-3);
`;

const StyledCalloutText = styled(Callout.Text)`
  ${props =>
    props.showFullText
      ? css`
          width: 100%;
        `
      : css`
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        `}
`;

const StyledClickableCalloutText = styled(Callout.Text)`
  cursor: pointer;
`;

function formatValidationError(response, { oneBasedRow = false } = {}) {
  if (response?.status !== 400) return null;

  const message = response?.data?.details?.message || response?.data?.error;
  if (!message) return null;

  const regex = /^"?networks\[(\d+)\]\.(\w+)"?\s*(.*)$/;
  const match = message.match(regex);
  if (!match) return message;

  const [, rawRow, column, rest] = match;
  const row = oneBasedRow ? Number(rawRow) + 1 : Number(rawRow);
  const columnHeader = column.toUpperCase();
  return `Error in row ${row} in column ${columnHeader} - ${rest}`.trim();
}

function ErrorCallout({ error }) {
  const ref = useRef(null);
  const [shouldExpand, setShouldExpand] = useState(false);
  const [showFullText, setShowFullText] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const check = () => {
      setShouldExpand(el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight);
    };

    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const errorMessage = getErrorMessageFromAPIError(error);
  const errorMessageText = errorMessage || error.message;
  const errorText = formatValidationError(error.response) ?? errorMessageText;

  return (
    <StyledCalloutRoot
      size='1'
      color='red'
      my='4'
    >
      <Callout.Icon>
        <Icon
          name='slash-circle-01'
          width={16}
          height={16}
          color='var(--error-11)'
        />
      </Callout.Icon>
      <Box overflow='hidden'>
        <Callout.Text weight='bold'>An error occurred:</Callout.Text>
        <Box overflow='hidden'>
          <Flex width='100%'>
            <StyledCalloutText
              showFullText={showFullText}
              ref={ref}
            >
              {errorText}
            </StyledCalloutText>
            {!showFullText && shouldExpand && (
              <StyledClickableCalloutText
                weight='bold'
                onClick={() => setShowFullText(true)}
              >
                more
              </StyledClickableCalloutText>
            )}
          </Flex>
        </Box>
        {showFullText && shouldExpand && (
          <StyledClickableCalloutText
            weight='bold'
            onClick={() => setShowFullText(false)}
          >
            Show less
          </StyledClickableCalloutText>
        )}
      </Box>
    </StyledCalloutRoot>
  );
}

ErrorCallout.propTypes = {
  error: PropTypes.object,
};

export default ErrorCallout;
